




































import { computed, defineComponent, onBeforeUnmount, ref, watch } from "@vue/composition-api";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useSystemSettings from "@/hooks/useSystemSettings";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { CreatePostData, PreventLogout } from "@/types";
import { EVENTS, PreventLogoutType } from "@/const";
import CreateStoryView from "@/components/PostCreate/views/CreateStoryView";
import ConfirmSendForVerification from "./components/ConfirmSendForVerification";
import usePostData from "./hooks/usePostData";
import usePostFiles from "./hooks/usePostFiles";
import { usePreventPublish } from "./hooks/usePreventPublish";
import { UpdatePostData } from "./types";
import CreatePostMinimizedView from "./views/CreatePostMinimizedView";
import EventCreateView from "./views/EventCreateView";
import MediaPostCreateView from "./views/MediaPostCreateView";
import TextPostCreateView from "./views/TextPostCreateView";

export default defineComponent({
  name: "PostCreate",
  components: { ConfirmSendForVerification, CreatePostMinimizedView },
  setup() {
    const {
      postData,
      isDraft,
      originalPostDescription,
      originalPostTitle,
      getPostData,
      updatePost,
      deletePost,
      onPartialUpdate,
    } = usePostData();
    const { cancelPostUploadMethods, resetPreventPublish, watchExecution } = usePreventPublish();
    const { systemSettings } = useSystemSettings();

    const postId = ref("");

    const {
      postFiles,
      errorUploadingFile,
      loadingUploadUrls,
      postFileDataList,
      originalPostFilesIds,
      uploadedFilesNumber,
      isUploadingFiles,
      getPostFiles,
      updatePostFiles,
      onAddFiles,
      onAddFilesFromVault,
      onFileRemove,
      onFileChange,
      onDraggableSort,
      stopAndRemoveUploadingFiles,
      resetUnsavedFilesQueue,
    } = usePostFiles(
      computed(() => postId.value),
      computed(() => postData.value?.type || ""),
      computed(() => !isDraft.value)
    );

    const loading = ref(false);
    const userId = ref("");
    const showCreate = ref(false);
    const showMinimizedView = ref(false);
    const showEditConfirm = ref(false);
    const updateCb = ref<(() => Promise<void>)[]>([]);
    const savingPromise = ref<Promise<any> | null>(null);
    const saving = ref(false);
    const updatePostData = ref<UpdatePostData | null>(null);
    const isPublished = ref(false);

    const isPostEmpty = computed(() => {
      if (!postData.value) return true;
      return !(postData.value.description || postData.value.title || postFileDataList.value.length > 0);
    });

    const shouldShowUpdateConfirm = computed(() => systemSettings.value?.content?.postVerification);

    const component = computed(() => {
      if (!postData.value) return;
      switch (postData.value.type) {
        case ContentFeedTypeEnum.STORY:
          return CreateStoryView;
        case ContentFeedTypeEnum.RICH_TEXT:
          return TextPostCreateView;
        case ContentFeedTypeEnum.EVENT:
          return EventCreateView;
        default:
          return MediaPostCreateView;
      }
    });

    const showCreatePost = (data?: CreatePostData) => {
      if (!data) return;
      postId.value = data.postId;
      userId.value = data.userId;
      showCreate.value = true;
    };

    const onEditClick = () => {
      showMinimizedView.value = false;
      saving.value = false;
      cancelPostUploadMethods();
    };

    const showPublishedMinimized = async () => {
      isPublished.value = true;
      await new Promise(resolve => setTimeout(resolve, 3000));
    };

    const onSetUpdateCb = (cb: () => Promise<void>) => {
      updateCb.value.push(cb);
    };

    const reset = () => {
      if (postData.value && postData.value.type !== ContentFeedTypeEnum.STORY && !isDraft.value) {
        emitter.emit(EVENTS.CONTENT_FEED_UPDATED, { userId: postData.value.userId, feedId: postData.value.id });
      }
      emitter.emit(EVENTS.PREVENT_LOGOUT, {
        type: PreventLogoutType.CREATE_POST,
        value: false,
      });
      stopAndRemoveUploadingFiles().then();
      resetUnsavedFilesQueue();
      resetPreventPublish();
      showCreate.value = false;
      showMinimizedView.value = false;
      isPublished.value = false;
      postData.value = null;
      postId.value = "";
      userId.value = "";
      updateCb.value = [];
    };

    const onUpdate = async ({ data, closeOnSave, beforeUpdate, afterUpdate }: UpdatePostData) => {
      let deferred: Function = () => {};
      try {
        errorUploadingFile.value = false;
        savingPromise.value = new Promise<void>(r => (deferred = r));
        saving.value = !!closeOnSave;
        beforeUpdate && (await beforeUpdate());
        await Promise.all(updateCb.value.map(i => i()));
        cancelPostUploadMethods();

        if (isUploadingFiles.value) {
          const preventLogout: PreventLogout = {
            type: PreventLogoutType.CREATE_POST,
            value: true,
            text: $t("Please do not leave the website until files are uploaded"),
            cb: () => {
              cancelPostUploadMethods();
              reset();
            },
          };
          emitter.emit(EVENTS.PREVENT_LOGOUT, preventLogout);
          showMinimizedView.value = true;
        }

        const canContinue = await watchExecution(
          new Promise<void>((resolve, reject) => {
            updatePostFiles().then(() => (errorUploadingFile.value ? reject() : resolve()));
          })
        );
        if (!canContinue) {
          return;
        }

        await updatePost(data);
        if (closeOnSave) {
          if (showMinimizedView.value && !isDraft.value) {
            await showPublishedMinimized();
          }
          reset();
        }
      } finally {
        afterUpdate && afterUpdate();
        deferred();
        savingPromise.value = null;
        saving.value = false;
      }
    };

    const onUpdateData = async (updateData: UpdatePostData) => {
      if (!postData.value) return;
      if (
        shouldShowUpdateConfirm.value &&
        !isDraft.value &&
        (updateData.data.description !== originalPostDescription.value ||
          updateData.data.title != originalPostTitle.value ||
          postFileDataList.value.some(file => !originalPostFilesIds.value.includes(file.id)))
      ) {
        showEditConfirm.value = true;
        updatePostData.value = updateData;
      } else {
        await onUpdate(updateData);
      }
    };

    const saveDraft = async () => {
      return onUpdate({
        data: { published: false },
        closeOnSave: true,
      });
    };

    const deleteDraft = async () => {
      await savingPromise.value;
      await deletePost();
      reset();
    };

    const onCloseModal = async () => {
      if (saving.value) return;
      if (postData.value && isDraft.value) {
        isPostEmpty.value ? await deleteDraft() : await saveDraft();
      } else {
        reset();
      }
    };

    const onConfirmSendUpdate = async () => {
      if (!updatePostData.value) return;
      showEditConfirm.value = false;
      await onUpdate(updatePostData.value);
      updatePostData.value = null;
    };

    const onCancelSendUpdate = async () => {
      updatePostData.value?.afterUpdate && updatePostData.value.afterUpdate();
      showEditConfirm.value = false;
      updatePostData.value = null;
    };

    const watchPostId = async (postId: string) => {
      if (!postData.value && postId) {
        try {
          loading.value = true;
          await Promise.all([getPostData({ userId: userId.value, postId }), getPostFiles(postId)]);
        } finally {
          loading.value = false;
        }
      }
    };

    watch(postId, watchPostId, { immediate: true });

    emitter.on<CreatePostData>(EVENTS.SHOW_CREATE_POST, showCreatePost);
    onBeforeUnmount(() => {
      emitter.off<CreatePostData>(EVENTS.SHOW_CREATE_POST, showCreatePost);
    });

    return {
      loading,
      showCreate,
      component,
      postData,
      postFileDataList,
      postFiles,
      showMinimizedView,
      uploadedFilesNumber,
      errorUploadingFile,
      loadingUploadUrls,
      updatePostData,
      showEditConfirm,
      saving,
      isPublished,
      isDraft,
      onCloseModal,
      onEditClick,
      onAddFiles,
      onAddFilesFromVault,
      onFileRemove,
      onFileChange,
      onSetUpdateCb,
      onUpdateData,
      updatePost,
      onCancelSendUpdate,
      onConfirmSendUpdate,
      onDraggableSort,
      onPartialUpdate,
    };
  },
});
